import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body3 } from '@/components/typography';
import LogoUploader from '@/components/uploader/logo-uploader.component';
import { cn } from '@/lib/utils';
import { useCustomer } from '@/modules/Customers/Customers.api';
import { routes } from '@/modules/Router';
import axios from 'axios';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export function EditCustomerForm() {
  const { customerId } = useParams<{ customerId: string }>();
  const [presignedLogoUrl, setPresignedLogoUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isFetchingPresignedLink, setIsFetchingPresignedLink] = useState(false);

  const navigate = useNavigate();

  const { data: customer, status } = useCustomer(customerId);

  if (status === 'error') {
    toast.error('Failed to fetch customer details');
    return;
  }

  if (status === 'pending') {
    return <Loading />;
  }

  if (customer === undefined || customer === null) {
    toast.error('Failed to fetch customer details');
    return;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!presignedLogoUrl || !file) {
      toast.error('Please upload a logo before submitting.');
      return;
    }

    try {
      setIsLoading(true);

      // URL is local if it starts with /media/ or a full S3 URL
      const isLocalUrl = presignedLogoUrl.startsWith('/media/');
      // For local URLs, we need to prepend the backend API URL
      const uploadUrl = isLocalUrl ? `${import.meta.env.VITE_API_BASE_URI}${presignedLogoUrl}` : presignedLogoUrl;

      const response = await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Customer logo updated successfully!');
        navigate(routes.customers);
      }
    } catch (error) {
      console.error('Error updating customer logo:', error);
      toast.error('Failed to update customer logo. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('w-full h-full bg-secondary-15 items-center p-16 place-items-center mb-8')}>
      <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6 min-w-[450px]')}>
        <div>
          <h2 className={cn('text-lg font-semibold text-gray-700')}>Customer Details</h2>
          <Body3 className="text-neutral-500">Edit customer details below.</Body3>
        </div>

        <div className={cn('flex flex-row')}>
          <label htmlFor="customer-name" className={cn('text-sm font-medium text-gray-600 mb-2')}>
            Customer Name:
          </label>
          &nbsp;
          <label htmlFor="customer-name" className={cn('text-sm font-bold text-gray-600 mb-2')}>
            {customer.client_name}
          </label>
        </div>

        <LogoUploader
          customerId={customer.client_id}
          setPresignedLogoUrl={setPresignedLogoUrl}
          existingLogo={file}
          setFile={setFile}
          setIsFetchingPresignedLink={setIsFetchingPresignedLink}
        />

        <div className={cn('flex flex-row gap-4')}>
          <Link className="w-1/2" to={routes.customers}>
            <SecondaryButton>Cancel</SecondaryButton>
          </Link>
          <PrimaryButton className="w-1/2" type="submit" disabled={isLoading || isFetchingPresignedLink || !customer.client_id || !file}>
            Confirm Changes
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
